import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/users';
import { GlobalRole, User } from '@/types';
import { Ban, Trash2, Copy, Check, MailCheck, MailX } from 'lucide-react';
import { format } from 'date-fns';
import clsx from 'clsx';

const ROLE_BADGE: Record<GlobalRole, string> = {
  SUPER_ADMIN: 'bg-yellow-700 text-yellow-100',
  ADMIN: 'bg-red-800 text-red-100',
  USER: 'bg-surface-700 text-surface-200',
  BANNED: 'bg-red-950 text-red-300',
};

export default function AdminUsers() {
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const qc = useQueryClient();

  // Phase 12.1 helper — SUPER_ADMIN'у часто нужно скопировать user ID, чтобы
  // вставить как ownerUserId при создании workspace. Tap → copy → 1.5 s tick.
  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
    } catch {
      // Fallback: do nothing — user can select-and-copy manually
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', search, cursor],
    queryFn: () => adminApi.listUsers({ search, cursor, limit: 30 }),
  });

  const banMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => adminApi.banUser(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const unbanMut = useMutation({
    mutationFn: (id: string) => adminApi.unbanUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const roleMut = useMutation({
    mutationFn: ({ id, role }: { id: string; role: GlobalRole }) => adminApi.setUserRole(id, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  // Phase 13 — manual email-verify эскейп-хатч (когда SMTP не дошло).
  const verifyMut = useMutation({
    mutationFn: (id: string) => adminApi.verifyUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const handleBan = (u: User & { email: string }) => {
    const reason = prompt(`Ban reason for ${u.username}:`) ?? undefined;
    banMut.mutate({ id: u.id, reason });
  };

  const handleDelete = (u: User & { email: string }) => {
    if (!confirm(`Permanently delete ${u.username}? This cannot be undone.`)) return;
    deleteMut.mutate(u.id);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-surface-50">Users</h1>
        <input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCursor(undefined); }}
          className="irc-input w-64"
          placeholder="Search users…"
        />
      </div>

      {isLoading ? (
        <div className="text-surface-500">Loading…</div>
      ) : (
        <>
          <div className="bg-surface-900 border border-surface-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-800">
                  <th className="text-left px-4 py-3 text-surface-400 font-medium">User</th>
                  <th className="text-left px-4 py-3 text-surface-400 font-medium">Email</th>
                  <th className="text-left px-4 py-3 text-surface-400 font-medium">Verified</th>
                  <th className="text-left px-4 py-3 text-surface-400 font-medium">Role</th>
                  <th className="text-left px-4 py-3 text-surface-400 font-medium">Joined</th>
                  <th className="text-left px-4 py-3 text-surface-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.data.map((u) => (
                  <tr key={u.id} className="border-b border-surface-800/50 hover:bg-surface-800/30">
                    <td className="px-4 py-3">
                      <p className="font-medium text-surface-100">{u.displayName}</p>
                      <p className="text-surface-500 text-xs font-mono">@{u.username}</p>
                      <button
                        onClick={() => copyId(u.id)}
                        title="Скопировать ID"
                        className="mt-0.5 text-surface-600 hover:text-brand-400 text-[10px] font-mono inline-flex items-center gap-1 transition-colors"
                      >
                        {copiedId === u.id ? (
                          <><Check size={10} className="text-emerald-400" /> скопировано</>
                        ) : (
                          <><Copy size={10} /> {u.id}</>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-surface-400 text-xs">{u.email}</td>
                    <td className="px-4 py-3">
                      {u.emailVerified ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400 text-xs" title="Email подтверждён">
                          <MailCheck size={13} /> ✓
                        </span>
                      ) : (
                        <button
                          onClick={() => verifyMut.mutate(u.id)}
                          disabled={verifyMut.isPending}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-900/30 text-amber-300 hover:bg-amber-900/60 text-xs transition-colors"
                          title="Подтвердить email вручную"
                        >
                          <MailX size={13} /> Verify
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.globalRole}
                        onChange={(e) => roleMut.mutate({ id: u.id, role: e.target.value as GlobalRole })}
                        className={clsx('badge cursor-pointer', ROLE_BADGE[u.globalRole])}
                      >
                        {(['USER', 'ADMIN', 'SUPER_ADMIN', 'BANNED'] as GlobalRole[]).map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-surface-500 text-xs">
                      {format(new Date(u.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {u.globalRole === 'BANNED' ? (
                          <button
                            onClick={() => unbanMut.mutate(u.id)}
                            className="btn-ghost text-xs px-2 py-1 text-green-400"
                          >
                            Unban
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBan(u)}
                            className="btn-ghost text-xs px-2 py-1 text-red-400"
                          >
                            <Ban size={13} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(u)}
                          className="btn-ghost text-xs px-2 py-1 text-red-500"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setCursor(undefined)}
              disabled={!cursor}
              className="btn-ghost text-xs"
            >
              ← First
            </button>
            {data?.nextCursor && (
              <button
                onClick={() => setCursor(data.nextCursor ?? undefined)}
                className="btn-ghost text-xs"
              >
                Next →
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
